import IconButton from 'components/IconButton';
import LoadingScreen from 'components/LoadingScreen';
import { atom, useAtom, useAtomValue } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import React from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Collapse,
  Row,
} from 'reactstrap';

import {
  appliedIntegrationIdsAtom,
  providerFiltersAtom,
  severityFiltersAtom,
} from '../state';
import MetricsChart from './MetricsChart';
import { MetricsView } from './types';
import { useMetricsData } from './useMetricsData';

const storedViewAtom = atomWithStorage<MetricsView>(
  'bpa_metrics_view',
  'Severity',
);
// Tolerate an obsolete preference without changing the persisted data contract.
const metricsViewAtom = atom(
  (get): MetricsView =>
    get(storedViewAtom) === 'Timeband' ? 'Timeband' : 'Severity',
  (_get, set, value: MetricsView) => set(storedViewAtom, value),
);
const isMetricsSectionOpenAtom = atom(false);

export default function VulnMetrics() {
  const [isOpen, setOpen] = useAtom(isMetricsSectionOpenAtom);
  const [view, setView] = useAtom(metricsViewAtom);
  const providers = useAtomValue(providerFiltersAtom);
  const severities = useAtomValue(severityFiltersAtom);
  const integrationIds = useAtomValue(appliedIntegrationIdsAtom);
  const { data, loading, error, retry } = useMetricsData(isOpen);

  return (
    <Card>
      <CardHeader>
        <Row>
          <Col className="col-auto">
            <CardTitle tag="h4">
              Finding&nbsp;Metrics&nbsp;-&nbsp;{view}
              <Button
                outline
                color="primary"
                size="sm"
                aria-expanded={isOpen}
                aria-controls="bpa-metrics-content"
                aria-label={
                  isOpen ? 'Collapse Finding metrics' : 'Expand Finding metrics'
                }
                onClick={() => setOpen((value) => !value)}
              >
                <i
                  className={`bi bi-chevron-${isOpen ? 'up' : 'down'}`}
                  aria-hidden="true"
                />
              </Button>
            </CardTitle>
          </Col>
          <Col>
            <Collapse isOpen={isOpen}>
              <div className="d-inline float-right">
                <IconButton
                  id="bpaMetricsViewButton"
                  iconElement={
                    <i
                      className={
                        view === 'Severity'
                          ? 'fa fa-clock-four fa-lg'
                          : 'fa fa-chart-bar fa-lg'
                      }
                      role="img"
                      aria-label={
                        view === 'Severity'
                          ? 'Time-Based View'
                          : 'Severity View'
                      }
                    />
                  }
                  tooltip={
                    view === 'Severity' ? 'Time-Based View' : 'Severity View'
                  }
                  onClick={() =>
                    setView(view === 'Severity' ? 'Timeband' : 'Severity')
                  }
                />
              </div>
            </Collapse>
          </Col>
        </Row>
      </CardHeader>
      <Collapse isOpen={isOpen} id="bpa-metrics-content">
        <CardBody>
          {loading && <LoadingScreen />}
          {error && (
            <div role="alert">
              {error}{' '}
              <Button size="sm" onClick={retry}>
                Retry
              </Button>
            </div>
          )}
          {data && (
            <div className="row">
              {(['open', 'closed'] as const).map((status) => (
                <div className="col-md-6" key={status}>
                  <MetricsChart
                    source={data}
                    status={status}
                    view={view}
                    providers={providers}
                    severities={severities}
                    integrationIds={integrationIds}
                  />
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Collapse>
    </Card>
  );
}
